import React, { useEffect, useMemo, useState } from "react";
import DsrReportDetail from "./DsrReportDetail";

const DEFAULT_PAGE_SIZE = 25;

function money(value) {
  return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function shortDate(value) {
  if (!value) return "-";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function shortDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function roleLabel(role) {
  if (role === "sr_lgs") return "SR. LGS";
  if (role === "sr_bde") return "SR. BDE";
  return String(role || "").toUpperCase() || "-";
}

function roleClass(role) {
  return String(role || "agent").toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function numberValue(value) {
  return Number(value || 0);
}

function getDials(report) {
  return numberValue(report.dials);
}

function getConnects(report) {
  if (report.dsrRole === "lgs" || report.dsrRole === "sr_lgs") return numberValue(report.fields?.inboundCalls ?? report.connects);
  return numberValue(report.connects);
}

function getNewLeads(report) {
  return numberValue(report.newLeads);
}

function getDeals(report) {
  if (report.dsrRole === "lgs") return numberValue(report.fields?.callBacks);
  if (report.dsrRole === "sr_lgs") return numberValue(report.fields?.srLgsDealsClosed);
  return numberValue(report.dealsClosed);
}

function getArr(report) {
  return numberValue(report.arrClosed);
}

function reviewStatus(report) {
  if (!["bde", "lgs", "sr_lgs"].includes(report.dsrRole)) return "Not Applicable";
  return report.managerReviewStatus === "reviewed" ? "Reviewed" : "Pending";
}

function statusClass(label) {
  return String(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

function sortReports(reports, sortConfig) {
  const sortable = [...reports];
  const direction = sortConfig.direction === "asc" ? 1 : -1;

  sortable.sort((left, right) => {
    const getters = {
      agent: (item) => String(item.agentName || "").toLowerCase(),
      team: (item) => String(item.agentTeam || "").toLowerCase(),
      role: (item) => String(item.dsrRole || ""),
      date: (item) => String(item.businessDate || ""),
      activity: getDials,
      connects: getConnects,
      newLeads: getNewLeads,
      deals: getDeals,
      arr: getArr,
      review: reviewStatus,
      submitted: (item) => String(item.submittedAt || "")
    };
    const getter = getters[sortConfig.key] || getters.submitted;
    const leftValue = getter(left);
    const rightValue = getter(right);

    if (leftValue > rightValue) return direction;
    if (leftValue < rightValue) return -direction;
    return 0;
  });

  return sortable;
}

function paginate(items, page, pageSize) {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount);
  return {
    page: safePage,
    pageCount,
    rows: items.slice((safePage - 1) * pageSize, safePage * pageSize)
  };
}

export default function DsrReportsWorkspace({
  reports,
  summary,
  filters,
  setFilters,
  loading,
  error,
  onClose,
  closeLabel = "Back to Dashboard",
  onFetchReports,
  onResetFilters,
  onDownloadCsv,
  approvals,
  approvalsLoading,
  approvalsError,
  reviewingApprovalId,
  onReviewApproval,
  missingResult,
  missingLoading,
  missingError,
  onFetchMissing,
  onDownloadMissingCsv,
  selectedReportId,
  setSelectedReportId,
  deletingReportId,
  onDeleteReport,
  onSaveManagerReview,
  savingManagerReviewId,
  canDeleteReports = true,
  canManageReviews = true,
  workspaceMode = "manager"
}) {
  const [activeTab, setActiveTab] = useState("daily");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [submissionFilter, setSubmissionFilter] = useState("submitted");
  const [sortConfig, setSortConfig] = useState({ key: "submitted", direction: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [detailPreferredTab, setDetailPreferredTab] = useState("summary");
  const [selectedApproval, setSelectedApproval] = useState(null);

  const selectedReport = useMemo(
    () => reports.find((report) => report.id === selectedReportId) || null,
    [reports, selectedReportId]
  );

  const pendingReviewReports = useMemo(
    () => reports.filter((report) => ["bde", "sr_bde", "lgs", "sr_lgs"].includes(report.dsrRole) && report.managerReviewStatus !== "reviewed"),
    [reports]
  );

  const filteredReports = useMemo(() => {
    if (reviewFilter === "reviewed") {
      return reports.filter((report) => ["bde", "lgs", "sr_lgs"].includes(report.dsrRole) && report.managerReviewStatus === "reviewed");
    }
    if (reviewFilter === "pending") {
      return pendingReviewReports;
    }
    return reports;
  }, [pendingReviewReports, reports, reviewFilter]);

  const activeReports = activeTab === "pending" ? pendingReviewReports : filteredReports;
  const sortedReports = useMemo(() => sortReports(activeReports, sortConfig), [activeReports, sortConfig]);
  const paged = paginate(sortedReports, page, pageSize);

  useEffect(() => {
    setPage(1);
  }, [activeTab, reviewFilter, submissionFilter, filters.from, filters.to, filters.role, filters.team, filters.agent, pageSize]);

  const dateRangeLabel = useMemo(() => {
    if (filters.from && filters.to && filters.from !== filters.to) {
      return `${shortDate(filters.from)} - ${shortDate(filters.to)}`;
    }
    return shortDate(filters.from || filters.to);
  }, [filters.from, filters.to]);

  const kpis = [
    ["total", "Total DSRs", summary.totalReports || reports.length || 0, "Submitted reports"],
    ["bde", "BDE", summary.bdeReports || 0, "BDE submissions"],
    ["sr-bde", "SR. BDE", summary.srBdeReports || 0, "SR. BDE submissions"],
    ["lgs", "LGS", summary.lgsReports || 0, "LGS submissions"],
    ["sr-lgs", "SR. LGS", summary.srLgsReports || 0, "SR. LGS submissions"],
    ["pending", "Pending Reviews", pendingReviewReports.length, "Requires TL review"],
    ["missing", "Missing DSR", missingResult?.missingCount ?? "-", "Agents without submission"],
    ["arr", "ARR Closed", money(summary.totalArrClosed || 0), "Selected period"]
  ];

  const activeFilterChips = [
    filters.from ? ["from", `From ${shortDate(filters.from)}`] : null,
    filters.to ? ["to", `To ${shortDate(filters.to)}`] : null,
    filters.role && filters.role !== "all" ? ["role", roleLabel(filters.role)] : null,
    workspaceMode !== "tl" && filters.team && filters.team !== "all" ? ["team", filters.team] : null,
    filters.agent ? ["agent", `Agent: ${filters.agent}`] : null,
    reviewFilter !== "all" ? ["review", reviewFilter === "pending" ? "Pending Review" : "Reviewed"] : null,
    submissionFilter !== "submitted" ? ["submission", "Missing DSRs"] : null
  ].filter(Boolean);

  const updateSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc"
    }));
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const removeFilterChip = (key) => {
    if (key === "review") {
      setReviewFilter("all");
      return;
    }
    if (key === "submission") {
      setSubmissionFilter("submitted");
      return;
    }
    updateFilter(key, key === "role" || key === "team" ? "all" : "");
  };

  const applyFilters = () => {
    if (submissionFilter === "missing") {
      setActiveTab("missing");
      onFetchMissing?.(filters);
      return;
    }
    if (activeTab === "missing") setActiveTab("daily");
    onFetchReports(filters);
  };

  const resetFilters = () => {
    setReviewFilter("all");
    setSubmissionFilter("submitted");
    setActiveTab("daily");
    setSelectedReportId("");
    setSelectedApproval(null);
    onResetFilters();
  };

  const openReport = (report, preferredTab = "summary") => {
    setDetailPreferredTab(preferredTab);
    setSelectedApproval(null);
    setSelectedReportId(report.id);
  };

  const openApproval = (request) => {
    setSelectedReportId("");
    setSelectedApproval(request);
  };

  const closeDrawer = () => {
    setSelectedReportId("");
    setSelectedApproval(null);
  };

  const selectTab = (tab) => {
    setActiveTab(tab);
    setSubmissionFilter(tab === "missing" ? "missing" : "submitted");
    closeDrawer();
    if (tab === "missing") onFetchMissing?.(filters);
  };

  const deleteReport = (report) => {
    if (!canDeleteReports) return;
    const ok = window.confirm(`Delete DSR for ${report.agentName} on ${report.businessDate}?`);
    if (ok) onDeleteReport(report);
  };

  const reviewApproval = (request, action) => {
    if (!request?.id) return;

    if (action === "approve") {
      const note = window.prompt("Approval reason is required before accepting this low-dial DSR.");
      if (!note || !note.trim()) return;
      onReviewApproval(request.id, action, note.trim());
      return;
    }

    const note = window.prompt("Rejection reason (optional):") || "";
    onReviewApproval(request.id, action, note.trim());
  };

  const renderSortLabel = (key, label) => (
    <button type="button" onClick={() => updateSort(key)} aria-label={`Sort by ${label}`}>
      {label}
      {sortConfig.key === key && <span className="dsr-sort-indicator">{sortConfig.direction === "asc" ? "ASC" : "DESC"}</span>}
    </button>
  );

  const renderReportTable = (rows) => (
    <section className="dsr-workspace-table-card" aria-label="Submitted DSR reports">
      {loading ? (
        <div className="dsr-workspace-empty">Loading DSR reports...</div>
      ) : rows.length === 0 ? (
        <div className="dsr-workspace-empty">No DSR reports found for this view.</div>
      ) : (
        <div className="dsr-workspace-table-wrap">
          <table className="dsr-workspace-table">
            <thead>
              <tr>
                <th>{renderSortLabel("agent", "Agent")}</th>
                <th>{renderSortLabel("team", "Team")}</th>
                <th>{renderSortLabel("role", "Role")}</th>
                <th>{renderSortLabel("date", "Date")}</th>
                <th className="numeric">{renderSortLabel("activity", "Activity")}</th>
                <th className="numeric">{renderSortLabel("connects", "Connects")}</th>
                <th className="numeric">{renderSortLabel("newLeads", "Leads")}</th>
                <th className="numeric">{renderSortLabel("deals", "Deals")}</th>
                <th className="numeric arr">{renderSortLabel("arr", "ARR")}</th>
                <th>{renderSortLabel("review", "Review Status")}</th>
                <th>{renderSortLabel("submitted", "Submitted")}</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((report) => {
                const isBde = report.dsrRole === "bde";
                const isReviewable = ["bde", "sr_bde", "lgs", "sr_lgs"].includes(report.dsrRole);
                const status = reviewStatus(report);
                const dials = getDials(report);
                const belowDials = isBde && dials < 150 && status !== "Approved Exception";

                return (
                  <tr
                    key={report.id}
                    className={selectedReportId === report.id ? "selected" : ""}
                    onClick={() => openReport(report)}
                  >
                    <td>
                      <div className="dsr-agent-cell">
                        <span className="dsr-agent-avatar">{initials(report.agentName)}</span>
                        <strong>{report.agentName}</strong>
                      </div>
                    </td>
                    <td>{report.agentTeam || "-"}</td>
                    <td><span className={`dsr-role-pill ${roleClass(report.dsrRole)}`}>{roleLabel(report.dsrRole)}</span></td>
                    <td>{shortDate(report.businessDate)}</td>
                    <td className={`numeric dsr-activity-cell${belowDials ? " warning" : ""}`}>
                      <strong>{dials.toLocaleString()}</strong>
                      <small>{isBde ? "Dials" : "Calls"}</small>
                      {belowDials && <em>Below 150</em>}
                    </td>
                    <td className="numeric">{getConnects(report).toLocaleString()}</td>
                    <td className="numeric">{getNewLeads(report).toLocaleString()}</td>
                    <td className="numeric">{getDeals(report).toLocaleString()}</td>
                    <td className="numeric arr">{money(getArr(report))}</td>
                    <td>
                      <span className={`dsr-review-status-badge ${statusClass(status)}`}>
                        <span aria-hidden="true" />
                        {status}
                      </span>
                    </td>
                    <td>{shortDateTime(report.submittedAt)}</td>
                    <td className="actions" onClick={(event) => event.stopPropagation()}>
                      <div className="dsr-row-actions">
                        <button type="button" onClick={() => openReport(report)}>
                          View
                        </button>
                        {canManageReviews && isReviewable && report.managerReviewStatus !== "reviewed" && (
                          <button type="button" className="review" onClick={() => openReport(report, "review")}>
                            Review
                          </button>
                        )}
                        {canDeleteReports && (
                          <button
                            type="button"
                            className="overflow"
                            title="Delete report"
                            aria-label={`Delete DSR for ${report.agentName}`}
                            disabled={deletingReportId === report.id}
                            onClick={() => deleteReport(report)}
                          >
                            {deletingReportId === report.id ? "..." : "Delete"}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="dsr-workspace-pagination">
        <span>
          Showing {sortedReports.length ? (paged.page - 1) * pageSize + 1 : 0}-
          {Math.min(paged.page * pageSize, sortedReports.length)} of {sortedReports.length}
        </span>
        <div className="dsr-pagination-controls">
          <label>
            Rows per page
            <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <button type="button" disabled={paged.page <= 1} onClick={() => setPage((current) => current - 1)}>
            Previous
          </button>
          <strong>{paged.page} / {paged.pageCount}</strong>
          <button type="button" disabled={paged.page >= paged.pageCount} onClick={() => setPage((current) => current + 1)}>
            Next
          </button>
        </div>
      </div>
    </section>
  );

  return (
    <section className={`dsr-workspace${workspaceMode === "tl" ? " dsr-workspace-tl" : ""}`} role="dialog" aria-modal="true" aria-label="Daily Sales Reports">
      <div className="dsr-workspace-shell">
        <header className="dsr-workspace-hero">
          <div>
            <span className="agent-dsr-eyebrow">{workspaceMode === "tl" ? "TL Review Activity" : "Manager workspace"}</span>
            <h1>{workspaceMode === "tl" ? "Team DSR Reviews" : "Daily Sales Reports"}</h1>
          </div>
          <div className="dsr-workspace-hero-actions">
            <span>{dateRangeLabel}</span>
            <button type="button" onClick={onDownloadCsv} disabled={reports.length === 0}>Export CSV</button>
            {onClose && (
              <button type="button" className="back" onClick={onClose}>{closeLabel}</button>
            )}
          </div>
        </header>

        <div className="dsr-workspace-kpis">
          {kpis.map(([tone, label, value, hint]) => (
            <article className={`dsr-workspace-kpi ${tone}`} key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
              <small>{hint}</small>
            </article>
          ))}
        </div>

        {workspaceMode === "tl" && summary.reviewActivity && (
          <dl className="tl-review-activity" aria-label="TL Review Activity">
            {[["Expected Reviews", "expectedReviews"], ["Reviews Completed", "reviewed"],
              ["Pending Reviews", "pendingReviews"], ["Approval Requests Handled", "approvalRequestsHandled"],
              ["Pending Approvals", "pendingApprovalRequests"]].map(([label, key]) => (
              <div key={key}><dt>{label}</dt><dd>{summary.reviewActivity[key] || 0}</dd></div>
            ))}
          </dl>
        )}
        <div className="dsr-workspace-toolbar">
          <label className="date-field">
            <span>From</span>
            <input type="date" value={filters.from} onChange={(event) => updateFilter("from", event.target.value)} />
          </label>
          <label className="date-field">
            <span>To</span>
            <input type="date" value={filters.to} onChange={(event) => updateFilter("to", event.target.value)} />
          </label>
          <label className="role-field">
            <span>Role</span>
            <select value={filters.role} onChange={(event) => updateFilter("role", event.target.value)}>
              <option value="all">All Roles</option>
              <option value="bde">BDE</option>
              <option value="sr_bde">SR. BDE</option>
              <option value="tl">TL</option>
              <option value="lgs">LGS</option>
              <option value="sr_lgs">SR. LGS</option>
              <option value="cpa">CPA</option>
              <option value="fs">FS</option>
            </select>
          </label>
          {workspaceMode !== "tl" && (
            <label className="team-field">
              <span>Team</span>
              <select value={filters.team || "all"} onChange={(event) => updateFilter("team", event.target.value)}>
                <option value="all">All Teams</option>
                <option value="Team Aaina">Team Aaina</option>
                <option value="Team Harsh">Team Harsh</option>
              </select>
            </label>
          )}
          <label className="agent-field">
            <span>Agent</span>
            <div className="dsr-search-input">
              <span aria-hidden="true">Search</span>
              <input
                type="search"
                value={filters.agent}
                onChange={(event) => updateFilter("agent", event.target.value)}
                placeholder="Search agent..."
              />
            </div>
          </label>
          <label className="review-field">
            <span>TL Review</span>
            <select value={reviewFilter} onChange={(event) => setReviewFilter(event.target.value)}>
              <option value="all">All</option>
              <option value="pending">Pending review</option>
              <option value="reviewed">Reviewed</option>
            </select>
          </label>
          <label className="submission-field">
            <span>Submission</span>
            <select value={submissionFilter} onChange={(event) => setSubmissionFilter(event.target.value)}>
              <option value="submitted">Submitted DSRs</option>
              <option value="missing">Missing DSRs</option>
            </select>
          </label>
          <button type="button" className="primary" onClick={applyFilters} disabled={loading || missingLoading}>
            {loading || missingLoading ? "Loading..." : "Apply Filters"}
          </button>
          <button type="button" className="reset" onClick={resetFilters}>Reset</button>
        </div>

        {activeFilterChips.length > 0 && (
          <div className="dsr-active-filters" aria-label="Active DSR filters">
            <span>Active filters:</span>
            {activeFilterChips.map(([key, label]) => (
              <button type="button" key={key} onClick={() => removeFilterChip(key)}>
                {label} x
              </button>
            ))}
            <button type="button" className="clear" onClick={resetFilters}>Clear all</button>
          </div>
        )}

        {(error || approvalsError || missingError) && (
          <div className="dsr-workspace-alert">
            {error || approvalsError || missingError}
          </div>
        )}

        <nav className="dsr-workspace-tabs" aria-label="DSR report views">
          <button type="button" className={activeTab === "daily" ? "active" : ""} onClick={() => selectTab("daily")}>
            Daily Reports <span>{reports.length}</span>
          </button>
          <button type="button" className={activeTab === "pending" ? "active" : ""} onClick={() => selectTab("pending")}>
            Pending Reviews <span className="pending">{pendingReviewReports.length}</span>
          </button>
          <button type="button" className={activeTab === "missing" ? "active" : ""} onClick={() => selectTab("missing")}>
            Missing DSR <span className="missing">{missingResult?.missingCount || 0}</span>
          </button>
          <button type="button" className={activeTab === "approvals" ? "active" : ""} onClick={() => selectTab("approvals")}>
            Approval Requests <span className="pending">{approvals.length}</span>
          </button>
        </nav>

        <main className="dsr-workspace-main">
          {(activeTab === "daily" || activeTab === "pending") && renderReportTable(paged.rows)}

          {activeTab === "missing" && (
            <section className="dsr-workspace-panel">
              <div className="dsr-workspace-panel-header">
                <div>
                  <h2>Missing DSR</h2>
                  <p>{missingResult ? `${shortDate(missingResult.from)} - ${shortDate(missingResult.to)}` : dateRangeLabel}</p>
                </div>
                <strong>{missingResult?.missingCount || 0} missing</strong>
              </div>
              <div className="dsr-workspace-inline-filters">
                <button type="button" onClick={onDownloadMissingCsv} disabled={!missingResult?.missingAgents?.length}>
                  Download Missing CSV
                </button>
              </div>

              {missingResult?.missingAgents?.length ? (
                <div className="dsr-workspace-table-wrap">
                  <table className="dsr-workspace-table missing-table">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Team</th>
                        <th>Role</th>
                        <th>Expected Date</th>
                        <th>Status</th>
                        <th>Reason / Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingResult.missingAgents.map((agent) => (
                        <tr key={`${agent.agentId}-${agent.businessDate}`}>
                          <td>
                            <div className="dsr-agent-cell">
                              <span className="dsr-agent-avatar">{initials(agent.agentName)}</span>
                              <strong>{agent.agentName}</strong>
                            </div>
                          </td>
                          <td>{agent.agentTeam || "-"}</td>
                          <td><span className={`dsr-role-pill ${roleClass(agent.dsrRole)}`}>{roleLabel(agent.dsrRole)}</span></td>
                          <td>{shortDate(agent.businessDate)}</td>
                          <td><span className="dsr-review-status-badge missing"><span aria-hidden="true" />Missing</span></td>
                          <td>Follow up with agent for the selected date.</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="dsr-workspace-empty">
                  {missingLoading ? 'Loading...' : missingResult ? 'No missing DSRs for this result.' : 'No result loaded.'}
                </div>
              )}
            </section>
          )}

          {activeTab === "approvals" && (
            <section className="dsr-workspace-panel">
              <div className="dsr-workspace-panel-header">
                <div>
                  <h2>Approval Requests</h2>
                  <p>BDE and Sr. BDE reports awaiting approval.</p>
                </div>
                <strong>{approvals.length} request{approvals.length === 1 ? "" : "s"}</strong>
              </div>
              {approvalsLoading ? (
                <div className="dsr-workspace-empty">Loading pending approvals...</div>
              ) : approvals.length === 0 ? (
                <div className="dsr-workspace-empty">No DSR approvals pending for this filter.</div>
              ) : (
                <div className="dsr-workspace-table-wrap">
                  <table className="dsr-workspace-table approvals-table">
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Team</th>
                        <th>Role</th>
                        <th>Date</th>
                        <th className="numeric">Dials</th>
                        <th>Reason</th>
                        <th className="numeric">Deals</th>
                        <th className="numeric arr">ARR</th>
                        <th>Requested</th>
                        <th>Status</th>
                        <th className="actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvals.map((request) => (
                        <tr
                          key={request.id}
                          className={selectedApproval?.id === request.id ? "selected" : ""}
                          onClick={() => openApproval(request)}
                        >
                          <td>
                            <div className="dsr-agent-cell">
                              <span className="dsr-agent-avatar">{initials(request.agentName)}</span>
                              <strong>{request.agentName}</strong>
                            </div>
                          </td>
                          <td>{request.agentTeam || "-"}</td>
                          <td><span className={`dsr-role-pill ${roleClass(request.dsrRole)}`}>{roleLabel(request.dsrRole)}</span></td>
                          <td>{shortDate(request.businessDate)}</td>
                          <td className="numeric dsr-activity-cell warning">
                            <strong>{Number(request.dials || 0).toLocaleString()}</strong>
                            <em>Below 150</em>
                          </td>
                          <td>{request.requestReason || "Below DSR threshold"}</td>
                          <td className="numeric">{request.dealsClosed || 0}</td>
                          <td className="numeric arr">{money(request.arrClosed || 0)}</td>
                          <td>{shortDateTime(request.requestedAt)}</td>
                          <td><span className={`dsr-review-status-badge ${statusClass(request.status)}`}><span aria-hidden="true" />{request.status || "pending"}</span></td>
                          <td className="actions" onClick={(event) => event.stopPropagation()}>
                            <button type="button" onClick={() => openApproval(request)}>Review Request</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {(selectedReport || selectedApproval) && (
        <aside className="dsr-workspace-drawer" aria-label="DSR details drawer">
          {selectedReport && (
            <DsrReportDetail
              report={selectedReport}
              onClose={closeDrawer}
              canManageManagerReview={canManageReviews}
              onSaveManagerReview={onSaveManagerReview}
              savingManagerReview={savingManagerReviewId === selectedReport.id}
              embedded
              preferredTab={detailPreferredTab}
            />
          )}

          {selectedApproval && (
            <div className="dsr-approval-drawer">
              <header>
                <div>
                  <span className="agent-dsr-eyebrow">Approval request</span>
                  <h2>{selectedApproval.agentName}</h2>
                  <p>{roleLabel(selectedApproval.dsrRole)} | {selectedApproval.agentTeam || "No Team"} | {shortDate(selectedApproval.businessDate)}</p>
                </div>
                <button type="button" onClick={closeDrawer} aria-label="Close approval request">Close</button>
              </header>
              <div className="dsr-approval-summary">
                <div><span>Dials</span><strong>{Number(selectedApproval.dials || 0).toLocaleString()}</strong></div>
                <div><span>Deals</span><strong>{selectedApproval.dealsClosed || 0}</strong></div>
                <div><span>ARR</span><strong>{money(selectedApproval.arrClosed || 0)}</strong></div>
                <div><span>Requested</span><strong>{shortDateTime(selectedApproval.requestedAt)}</strong></div>
                <div><span>Status</span><strong>{selectedApproval.status || "pending"}</strong></div>
                <div><span>Reviewed By</span><strong>{selectedApproval.reviewedBy || "-"}</strong></div>
                <div><span>Reviewer Team</span><strong>{selectedApproval.reviewerTeam || "-"}</strong></div>
                <div><span>Decision Date</span><strong>{shortDateTime(selectedApproval.reviewedAt)}</strong></div>
              </div>
              <section>
                <h3>Request Reason</h3>
                <p>{selectedApproval.requestReason || "Below DSR threshold"}</p>
              </section>
              {selectedApproval.reviewNote && (
                <section>
                  <h3>Decision Reason</h3>
                  <p>{selectedApproval.reviewNote}</p>
                </section>
              )}
              {selectedApproval.status === "pending" && (
                <footer>
                  <button
                    type="button"
                    className="approval-reject"
                    disabled={reviewingApprovalId === selectedApproval.id}
                    onClick={() => reviewApproval(selectedApproval, "reject")}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="approval-approve"
                    disabled={reviewingApprovalId === selectedApproval.id}
                    onClick={() => reviewApproval(selectedApproval, "approve")}
                  >
                    {reviewingApprovalId === selectedApproval.id ? "Saving..." : "Approve Exception"}
                  </button>
                </footer>
              )}
            </div>
          )}
        </aside>
      )}
    </section>
  );
}
